// Shared formatting for audit report summaries, details and exports.
const MARKER = '\n[AUDIT_DETAIL_V1:';
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const names = {
  address_line_1: 'Address line 1', address_line_2: 'Address line 2', zip: 'Postal code',
  IsActive: 'Active', is_enabled: 'Enabled', is_deleted: 'Deleted',
  kiosk_hours: 'Operating hours', payment_devices: 'Payment devices',
  pickup_switching: 'Pickup method switching', co_pay_amount: 'Copay amount',
  instamed_sso_alias: 'InstaMed SSO alias', hop_type: 'Schedule type',
  closed_msg: 'Closed message', closedimg_url: 'Closed image',
  parent_account_id: 'Account', box_id: 'Location', account_id: 'Account',
  badge_id: 'Badge ID', picture_url: 'Profile image', group_id: 'Group', preferred_location: 'Preferred location', roles: 'Roles', first_name: 'First name', last_name: 'Last name', openpathpassword: 'Open Path password',
  type_id: 'Type', location_type_id: 'Location type', role_id: 'Role',
};
function humanText(text) {
  return String(text == null ? '' : text)
    .replace(/\s*\([0-9a-f]{8}-[0-9a-f-]{27,}\)/gi, '')
    .replace(/(?:operation|session):?\s+[0-9a-f]{8}-[0-9a-f-]{27,}\.?/gi, '')
    .replace(UUID, '[internal reference]');
}
function label(path) {
  return humanText(path || '').split('.').map(key => names[key] || key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, c => c.toUpperCase())).join(' › ');
}
function parseDetail(description) {
  if (typeof description !== 'string') return null;
  // Compatibility for history already suffixed by the old phantom remover.
  description = description.replace(/(?: \[Removed phantom order: [^\]\r\n]*\])+$/, '');
  const index = description.lastIndexOf(MARKER);
  if (index < 0 || description.slice(-1) !== ']') return null;
  try {
    const detail = JSON.parse(description.slice(index + MARKER.length, -1));
    if (!detail || !['configuration', 'user', 'event'].includes(detail.kind) || typeof detail.summary !== 'string') return null;
    if ((detail.kind === 'configuration' || detail.sections !== undefined) && (!Array.isArray(detail.sections) || !detail.sections.every(section => section && typeof section.name === 'string' && Array.isArray(section.changes) && section.changes.every(change => change && typeof change.field === 'string')))) return null;
    if (detail.facts !== undefined && (!Array.isArray(detail.facts) || !detail.facts.every(fact => fact && typeof fact.label === 'string'))) return null;
    if (detail.failed_sections !== undefined && (!Array.isArray(detail.failed_sections) || !detail.failed_sections.every(name => typeof name === 'string'))) return null;
    if (detail.audit_failed_sections !== undefined && (!Array.isArray(detail.audit_failed_sections) || !detail.audit_failed_sections.every(name => typeof name === 'string'))) return null;
    return detail;
  } catch (_) { return null; }
}
function summary(description) {
  const detail = parseDetail(description);
  if (detail) return paymentSummary(detail) || humanText(detail.summary);
  // An invalid/future envelope must not dump machine payloads into a report cell.
  const text = String(description || '').split(MARKER)[0];
  return humanText(text);
}
function paymentSummary(detail) {
  if (detail.kind !== 'event' || !detail.target || detail.target.name !== 'Terminal payment') return null;
  const fact = name => ((detail.facts || []).find(item => item.label === name) || {}).value;
  const status = fact('Payment status');
  if (!['initiated', 'completed', 'failed', 'cancelled', 'unconfirmed'].includes(status)) return null;
  const window = fact('Window') || (detail.window && detail.window.windowName);
  const device = fact('Payment device');
  const reason = fact('Reason');
  return humanText(`Terminal payment ${status}${window ? ` at ${window}` : ''}${device ? ` using ${device}` : ''}.${reason ? ` Reason: ${reason}` : ''}`)
    .replace(/\[internal reference\]/g, '').replace(/\s+/g, ' ').trim();
}
function visibleFacts(detail) {
  return (detail.facts || []).filter(fact => !/^(?:\[internal reference\][\s,;]*)+$/i.test(valueText(fact.value).trim()));
}
function valueText(value, field) {
  if (value === null || value === undefined) return 'Not set';
  if (value === '') return 'Empty';
  if (value === true) return 'Yes';
  if (value === false) return 'No';
  if (Array.isArray(value)) return value.length ? value.map(item => valueText(item, field)).join('\n\n') : 'None';
  if (typeof value === 'object') {
    if (value.omitted === true && value.digest) return 'Large content changed (value not displayed)';
    const fields = Object.keys(value).filter(key => value[key] !== null && value[key] !== '' && (!/(^id$|_id$|_on$)/i.test(key) || !String(value[key] || '').match(UUID)));
    return fields.map(key => `${label(key)}: ${valueText(value[key], key)}`).join('\n') || 'No configured values (internal references hidden)';
  }
  if (/^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)$/i.test(String(field).split('.').pop())) {
    const parts = String(value).split(',');
    if (parts.length === 4) {
      if (parts[2] === 'true') return 'Closed';
      if (parts[3] === 'true') return 'Open all day';
      return parts[0] && parts[1] ? `${parts[0]} – ${parts[1]}` : 'Hours not set';
    }
  }
  return humanText(value);
}
function exportText(description) {
  const detail = parseDetail(description);
  if (!detail) return summary(description);
  const lines = [summary(description), `Outcome: ${detail.kind === 'event' ? 'Event recorded' : detail.outcome === 'partial' ? 'Partially saved' : detail.outcome === 'saved' ? 'Saved' : 'Final outcome unavailable'}`];
  if (detail.failed_sections && detail.failed_sections.length) lines.push('Sections not saved: ' + detail.failed_sections.map(humanText).join(', '));
  if (detail.audit_failed_sections && detail.audit_failed_sections.length) lines.push('Settings saved; audit details unavailable for: ' + detail.audit_failed_sections.map(humanText).join(', '));
  visibleFacts(detail).forEach(fact => lines.push(`${humanText(fact.label)}: ${valueText(fact.value)}`));
  (detail.sections || []).forEach(section => {
    lines.push(section.name);
    section.changes.forEach(change => lines.push(`${label(change.field)}: ${change.protected ? 'Changed (values protected)' : (detail.operation === 'create' ? '' : valueText(change.beforeLabel != null ? change.beforeLabel : change.before, change.field) + ' → ') + valueText(change.afterLabel != null ? change.afterLabel : change.after, change.field)}`));
  });
  if (Array.isArray(detail.fields) && detail.fields.length) lines.push('Settings changed: ' + detail.fields.map(label).join(', '));
  if (Array.isArray(detail.roles)) lines.push('Saved roles: ' + detail.roles.map(humanText).join(', '));
  return lines.join('\n');
}
function detailForRow(row) {
  const parsed = parseDetail(row && row.description);
  if (parsed) return parsed;
  if (!row || !row.description) return null;
  const recent = /^(Order (Stocked|Removed|Verified|Assigned|Updated|Held|Resumed|Release|Reminder)|Pickup (Started|Canceled|Timed Out|Completed)|Amount Due Updated|Offline Consultation|Phantom Removed|Bin Activation|Print|Admin)/i.test(row.action_name || '');
  if (!recent) return null;
  return { kind: 'event', summary: summary(row.description),
    target: { name: row.location_name || row.store_name || row.action_name },
    actor: { name: row.username || 'Actor not recorded' }, outcome: 'event',
    facts: [{ label: 'Action', value: row.action_name }, { label: 'Details', value: summary(row.description) }],
  };
}
function csvCell(value) {
  // Prevent values from breaking CSV columns or becoming spreadsheet formulas.
  const text = String(value == null ? '' : value);
  return '"' + (/^[\s]*[=+@-]/.test(text) ? "'" : '') + text.replace(/"/g, '""') + '"';
}
module.exports = { parseDetail, summary, humanText, label, valueText, exportText, csvCell, detailForRow, visibleFacts };



// WEBPACK FOOTER //
// ./src/utils/auditPresentation.js